import React, { useCallback, useMemo, useState } from "react";
import { DetailsLoader, SubmitButton, ToolTip } from "elements";
import i18n, { useTranslation } from "utilities/i18n";
import queryString from "query-string";
import {
  AddressInput,
  Form,
  Input,
  StandardFormSelect
} from "collections/Form";
import {
  useCreateRoomMutation,
  useDeviceDetailsQuery,
  useEditDeviceDetailsMutation,
  useUpdateSiteMutation
} from "generated/graphql";
import { SyncSiteAddress } from "routes/Manage/Register/RegisterDevice";
import { DetailProps } from "../View/DeviceDetails";
import { constructFilterList } from "utilities/filters/deviceFilters";
import { nameIsUnassigned } from "utilities/stringUtils";
import { ModalForm } from "collections/Modal";
import { DISMISS, useNotification } from "collections/Notifications";
import { Link, useHistory, useRouteMatch } from "polyrouter";
import useSitesAndRooms from "../../Register/useSitesAndRooms";
import * as yup from "yup";
import { InferType } from "yup";
import { protect } from "TsRoleGuards";
import { generatePath } from "react-router";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { UpdateDeviceRequest } from "generated/graphql.schema";

interface EditDetailsProps {
  device: DetailProps;
  refetch: () => void;
  loading: boolean;
}

const UpdateDeviceRequestSchema = yup.object({
  name: yup
    .string()
    .required()
    .ensure()
    .label(i18n.t("DEVICE_NAME")),
  site: yup
    .string()
    .required()
    .ensure()
    .label(i18n.t("SITE_NAME")),
  room: yup
    .string()
    .required()
    .ensure()
    .label(i18n.t("ROOM_NAME")),
  siteAddress: yup.object({
    address1: yup.string().label(i18n.t("ADDRESS")),
    geohash: yup.string().label(i18n.t("GEOHASH"))
  }),
  extras: yup.object({
    fromLookup: yup
      .boolean()
      .notRequired()
      .default(false)
  })
});
const UpdateDeviceFormSchema = UpdateDeviceRequestSchema;
type UpdateDeviceForm = InferType<typeof UpdateDeviceFormSchema>;

const EditDetailsLoader: React.FC<{ id: string }> = ({ id }) => {
  const [addNotification] = useNotification();
  const { t } = useTranslation();
  const { data, fetching, stale, executor } = useDeviceDetailsQuery({
    variables: {
      filter: {
        AND: constructFilterList({ id })
      }
    }
  });

  const deviceExists = Boolean(data?.devices) && Boolean(data?.devices[0]);
  const fallbackDevice = {} as DetailProps;
  const device = deviceExists
    ? data?.devices[0] ?? fallbackDevice
    : fallbackDevice;

  const refetch = useCallback(
    () => executor({ requestPolicy: "network-only" }),
    [executor]
  );

  if (!stale && !fetching && !deviceExists) {
    addNotification({
      type: "error",
      message: t("CANT_FIND_DEVICE"),
      dismiss: DISMISS.DEFAULT
    });
  }

  return (
    <EditDeviceDetailsModal
      device={device}
      refetch={refetch}
      loading={fetching}
    />
  );
};

export const EditDeviceDetailsModal: React.FC<EditDetailsProps> = ({
  device,
  refetch,
  loading
}) => {
  const { t } = useTranslation();
  const { push, location } = useHistory();
  const tenantId = useTenantId();
  const { url } = useRouteMatch();
  const { roomsRef, siteMeta, sitesWithNames } = useSitesAndRooms();
  const [siteId, setSiteId] = useState(device.site?.id);
  const site = useMemo(() => {
    const site = siteMeta?.sites.find(site => site.id === siteId);
    return site;
  }, [siteId, siteMeta]);

  const [siteAddressDisabled, setSiteAddressDisabled] = useState(true);

  const initialState = useMemo<UpdateDeviceForm>(
    () => ({
      ...UpdateDeviceFormSchema.default(),
      name: device.name || "",
      site: device.site?.name || "",
      room: device.room?.name || "",
      siteAddress: {
        address1: device.site?.address?.address1 || "",
        geohash: "" // NOTE: this will be set by the AddressInput component
      }
    }),
    [device]
  );

  const [, updateDevice] = useEditDeviceDetailsMutation();
  const [, updateSite] = useUpdateSiteMutation();
  const [, createRoom] = useCreateRoomMutation();

  const saveChanges = useCallback(
    async (values: UpdateDeviceForm) => {
      const fields: UpdateDeviceRequest = {};
      if (values.name !== device.name) {
        fields.name = values.name;
      }

      let siteId;
      if (!site) {
        await updateSite(
          {
            site: {
              name: values.site,
              tenantId,
              address: values.siteAddress
            }
          },
          {
            notify: {
              i18nKey: "ERROR_UPDATING_SITE",
              dismiss: DISMISS.USER_ONLY
            }
          }
        ).then(async ({ data }) => {
          const upsertedSite = data?.upsertSite;
          if (upsertedSite && upsertedSite.name !== device.site?.name) {
            fields.site = upsertedSite.name;
          }
          siteId = upsertedSite?.id;
        });
      } else {
        fields.site = site.name;
        siteId = site.id;
      }
      const selectedRoom = site?.rooms.find(room => room.name === values.room);
      if (selectedRoom) {
        if (values.room !== device?.room?.name) {
          fields.room = values.room ?? "Unassigned";
        }
      } else {
        if (siteId) {
          await createRoom(
            {
              fields: {
                siteId,
                name: values.room,
                tenantId
              }
            },
            {
              notify: {
                i18nKey: "ERROR_UPDATING_ROOM",
                dismiss: DISMISS.USER_ONLY
              }
            }
          )
            .then(({ data }) => {
              const newRoom = data?.upsertRoom;
              fields.room = newRoom?.name ?? "Unassigned";
            })
            .catch(() => {
              fields.room = "Unassigned";
            });
        }
      }

      const editDeviceResponse = await updateDevice(
        {
          id: device.id,
          fields
        },
        {
          notify: {
            i18nKey: "ERROR_UPDATING_DEVICE",
            dismiss: DISMISS.USER_ONLY
          }
        }
      );

      if (!editDeviceResponse.error) {
        push(
          generatePath(
            `${location.pathname.replace("/edit", "")}${
              location.search
            }&${queryString.stringify({
              editSuccess: true
            })}`
          )
        );
      }
      refetch();
    },
    [
      device,
      site,
      tenantId,
      refetch,
      createRoom,
      updateSite,
      updateDevice,
      location,
      push
    ]
  );

  const onSiteSelectionChange = useCallback(
    (
      siteName: string,
      setValues: (deviceFormValues: UpdateDeviceForm) => void,
      values: UpdateDeviceForm
    ) => {
      const updatedValues: UpdateDeviceForm = {
        ...values
      };
      const fallbackRoomName = "Unassigned";
      const selectedSite = siteMeta?.sites.find(site => site.name === siteName);
      setSiteId(selectedSite?.id);
      if (selectedSite) {
        const selectedSiteMatchesOriginal =
          selectedSite?.name === device.site?.name;
        updatedValues.room =
          (selectedSiteMatchesOriginal
            ? device.room?.name
            : selectedSite?.rooms[0]?.name) ?? fallbackRoomName;
      } else {
        updatedValues.room = fallbackRoomName;
      }

      const selectedSiteName = selectedSite?.name?.toLocaleLowerCase();

      setSiteAddressDisabled(Boolean(selectedSite));

      // If currentSiteName is undefined, it is because the user is creating a new/custom site
      if (
        nameIsUnassigned(selectedSiteName) ||
        !selectedSiteName ||
        selectedSiteName !== site?.name
      ) {
        updatedValues.siteAddress = {
          address1: "",
          geohash: ""
        };
      } else if (selectedSite) {
        updatedValues.siteAddress = {
          address1: selectedSite.address?.address1 ?? "",
          geohash: ""
        };
      }

      setValues(updatedValues);
    },
    [device, site, siteMeta, setSiteAddressDisabled, setSiteId]
  );

  return (
    <ModalForm<UpdateDeviceForm>
      title={t("EDIT_DEVICE_INFORMATION")}
      name="EditDeviceDetails"
      initialValues={initialState}
      validationSchema={UpdateDeviceFormSchema}
      onSubmit={saveChanges}
      enableReinitialize
      validateOnChange
    >
      {({ values, isSubmitting, setValues, isValid }) => {
        const cannotSubmit =
          loading ||
          !isValid ||
          (values.name === device.name && // NOTE: init values haven't changed
            values.room === device.room?.name &&
            values.site === device.site?.name);

        const updatedRooms = site?.rooms?.map(room => ({
          text: room.name,
          value: room.name
        })) ?? [
          {
            text: "Unassigned",
            value: "Unassigned"
          }
        ];
        roomsRef.current = updatedRooms;

        return (
          <div className="edit-content-container">
            <Form>
              <SyncSiteAddress
                initialValues={initialState}
                siteNameKey="site"
              />
              <DetailsLoader loading={loading} title={t("LOADING")}>
                <Input
                  label={t("DEVICE_NAME")}
                  name="name"
                  tabIndex={1}
                  disabled={isSubmitting}
                />
                <StandardFormSelect
                  placeholder={t("SITE_NAME")}
                  label={t("SITE_NAME")}
                  name="site"
                  options={sitesWithNames}
                  tabIndex={2}
                  disabled={isSubmitting}
                  onSelectionChange={siteName =>
                    onSiteSelectionChange(siteName, setValues, values)
                  }
                  customValue
                  search
                  selectOnInput
                />
                <StandardFormSelect
                  placeholder={t("ROOM_NAME")}
                  label={t("ROOM_NAME")}
                  name="room"
                  options={roomsRef.current}
                  tabIndex={3}
                  disabled={isSubmitting}
                  customValue
                  search
                  selectOnInput
                />
                {/* dropdown-group */}
                <div className="address-input-container">
                  <AddressInput
                    label={t("SITE_ADDRESS")}
                    tabIndex={4}
                    disabled={siteAddressDisabled || isSubmitting}
                    name="siteAddress.address1"
                    placeholder
                    placeholderLabel={
                      siteAddressDisabled ? t("UNKNOWN") : t("SITE_ADDRESS")
                    }
                    onSelect={address => {
                      setValues({
                        ...values,
                        siteAddress: {
                          address1: address.formattedAddress || "",
                          geohash: address.geohash || ""
                        }
                      });
                    }}
                  />
                  <div className="help-address">
                    <ToolTip
                      icon="help_circle"
                      iconTitle="help"
                      position="right"
                    >
                      {t(
                        siteAddressDisabled
                          ? "SITE_ADDRESS_TOOLTIP_DISABLED"
                          : "SITE_ADDRESS_TOOLTIP_ENABLED",
                        { site: values.site }
                      )}
                    </ToolTip>
                  </div>
                </div>
              </DetailsLoader>
              <div className="btn-container">
                <Link
                  persist={["DeviceFilterState", "InsightsFilterState"]}
                  to={url.replace("/edit", "")}
                  className="btn"
                  data-testid="close"
                  disabled={isSubmitting}
                >
                  {t("CANCEL")}
                </Link>
                <SubmitButton
                  isSubmitting={isSubmitting}
                  testId="save"
                  disabled={cannotSubmit}
                  labelKey="SAVE_CHANGES"
                  submittingLabelKey="SAVING"
                />
              </div>
            </Form>
          </div>
        );
      }}
    </ModalForm>
  );
};

export default protect("update:device")(EditDetailsLoader);
